import React, { useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useDataFetch } from '../../Hooks/Use-data-fetch';
import PageHeader from '../../Components/common/PageHeader';
import Hexagon from '../../Components/common/Hexagon';
import StatusChip from '../../Components/common/StatusChip';
import { API_BASE_URL, apiClient } from '../../api/api';
import { getLayoutedElements } from '../../utils/layout';

/**
 * @xyflow/react trae su propia paleta: aristas grises, minimapa azul y fondo
 * de cuadrícula. Nada de eso hereda la hoja de estilos (README, Advertencia
 * 5), así que los colores del diseño van explícitos aquí.
 */
const EDGE_COLOR = '#C9CDE4';
const DOT_COLOR = '#E4E7F2';

const EDGE_OPTIONS = {
  type: 'smoothstep',
  style: { stroke: EDGE_COLOR, strokeWidth: 1.5 },
};

const CustomNode = ({ data }) => {
  const { name, code, isActive, isRoot, onInfoClick, onNetworkClick, hasChildren } = data;

  return (
    <div
      className={[
        'ui-tree__node',
        isActive ? 'ui-tree__node--active' : '',
        isRoot ? 'ui-tree__node--root' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={`ui-tree__code${isRoot ? ' ui-tree__code--root' : ''}`}>
        {code}
      </div>
      <div className="ui-tree__name" title={name}>{name}</div>

      <div className="ui-tree__acts">
        <i
          className="ri-information-line"
          title="Ver información"
          onClick={onInfoClick}
        />
        {hasChildren && (
          <i
            className="ri-node-tree"
            title="Ver / plegar su red"
            onClick={onNetworkClick}
          />
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
      <Handle type="target" position={Position.Top} />
    </div>
  );
};

const NODE_TYPES = { custom: CustomNode };

export const ReferralNetwork = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getData } = useDataFetch();
  const [modalData, setModalData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadedParents, setLoadedParents] = useState(new Set());
  const [initialized, setInitialized] = useState(false);
  const [flow, setFlow] = useState(null);

  const { data: userData, isLoading: isUserLoading } = getData(
    ['user-by-id', id],
    `/users/byId/${id}`,
    { enabled: !!id }
  );
  const mainUser = userData?.data?.user;

  const { data: networkData, isLoading: isNetworkLoading } = getData(
    ['user-network', id],
    `/users/admin/network/with/id?userid=${id}`,
    { enabled: !!id }
  );

  const referralList = networkData?.data?.data ?? networkData?.data?.user ?? networkData?.data ?? [];

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const onLayout = (nodesToLayout, edgesToLayout) => {
    if (!nodesToLayout || nodesToLayout.length === 0) return;
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodesToLayout,
      edgesToLayout
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  };

  const toggleSubNetwork = async (parentId) => {
    if (loadedParents.has(parentId)) {
      const childrenIdsToRemove = new Set();
      const queue = [parentId];
      const visited = new Set([parentId]);

      while (queue.length > 0) {
        const currentId = queue.shift();
        const children = edges.filter(e => e.source === currentId).map(e => e.target);
        for (const childId of children) {
          if (!visited.has(childId)) {
            childrenIdsToRemove.add(childId);
            queue.push(childId);
            visited.add(childId);
          }
        }
      }

      const remainingNodes = nodes.filter(n => !childrenIdsToRemove.has(n.id));
      const remainingEdges = edges.filter(e => !childrenIdsToRemove.has(e.target));

      setLoadedParents(prev => {
        const newSet = new Set(prev);
        childrenIdsToRemove.forEach(id => newSet.delete(id));
        newSet.delete(parentId);
        return newSet;
      });

      onLayout(remainingNodes, remainingEdges);
      return;
    }

    try {
      const response = await apiClient.get(`/users/admin/network/with/id?userid=${parentId}`, {
        headers: {
          Authorization: `${localStorage.getItem("token") || ""}`,
          Accept: "application/json",
        },
      });

      const referrals = response?.data?.data?.data ?? response?.data?.data?.user ?? response?.data?.data ?? [];

      if (referrals.length === 0) return;

      const newNodes = referrals
        .filter(ref => !nodes.some(n => n.id === ref._id))
        .map((ref) => ({
          id: ref._id,
          type: 'custom',
          position: { x: 0, y: 0 },
          data: {
            name: ref.name,
            code: ref.multyhealth_code,
            isActive: ref.network?.loyalty_status === true,
            hasChildren: ref.referenced_by_count > 0,
            onInfoClick: () => {
              setModalData(ref);
              setModalOpen(true);
            },
            onNetworkClick: () => toggleSubNetwork(ref._id),
          },
        }));

      const newEdges = referrals
        .filter(ref => !edges.some(e => e.source === parentId && e.target === ref._id))
        .map((ref) => ({
          id: `e-${parentId}-${ref._id}`,
          source: parentId,
          target: ref._id,
        }));

      setNodes((currentNodes) => {
        const updatedNodes = [...currentNodes, ...newNodes];
        setEdges((currentEdges) => {
          const updatedEdges = [...currentEdges, ...newEdges];
          onLayout(updatedNodes, updatedEdges);
          return updatedEdges;
        });
        return updatedNodes;
      });

      setLoadedParents((prev) => new Set(prev).add(parentId));
    } catch (error) {
      console.error("Error loading sub network:", error);
    }
  };

  // Construir el árbol - Esperar a que los datos estén listos
  useEffect(() => {
    // No hacer nada si ya se inicializó o no hay usuario
    if (initialized || !mainUser) return;

    // Esperar a que ambas peticiones terminen
    if (isUserLoading || isNetworkLoading) return;

    const mainNode = {
      id: mainUser.id,
      type: 'custom',
      position: { x: 0, y: 0 },
      data: {
        name: mainUser.name,
        code: mainUser.multyhealth_code,
        isActive: mainUser.network?.loyalty_status === true,
        isRoot: true,
        hasChildren: (referralList && referralList.length > 0) || mainUser.referenced_by_count > 0,
        onInfoClick: () => {
          setModalData(mainUser);
          setModalOpen(true);
        },
        onNetworkClick: () => toggleSubNetwork(mainUser.id),
      },
    };

    let initialNodes = [mainNode];
    let initialEdges = [];

    if (referralList && referralList.length > 0) {
      const referralNodes = referralList.map((ref) => ({
        id: ref._id,
        type: 'custom',
        position: { x: 0, y: 0 },
        data: {
          name: ref.name,
          code: ref.multyhealth_code,
          isActive: ref.network?.loyalty_status === true,
          hasChildren: ref.referenced_by_count > 0,
          onInfoClick: () => {
            setModalData(ref);
            setModalOpen(true);
          },
          onNetworkClick: () => toggleSubNetwork(ref._id),
        },
      }));

      const referralEdges = referralList.map((ref) => ({
        id: `e-${mainUser.id}-${ref._id}`,
        source: mainUser.id,
        target: ref._id,
      }));

      initialNodes = [mainNode, ...referralNodes];
      initialEdges = referralEdges;

      setLoadedParents(new Set([mainUser.id]));
    }

    onLayout(initialNodes, initialEdges);
    setInitialized(true);
  }, [mainUser, referralList, isUserLoading, isNetworkLoading]);

  /**
   * Cuántos niveles hay DIBUJADOS ahora mismo.
   *
   * Antes esta cifra era un 7 escrito a mano. El árbol se despliega bajo
   * demanda, así que la profundidad real de la red no se conoce hasta abrirla
   * entera: lo honesto es contar lo que está a la vista y decirlo.
   */
  const levelsShown = useMemo(() => {
    if (nodes.length === 0) return 0;
    const parentOf = new Map(edges.map((e) => [e.target, e.source]));
    let deepest = 1;

    for (const node of nodes) {
      let depth = 1;
      let current = node.id;
      // El tope corta un ciclo si el backend devolviera una red circular.
      while (parentOf.has(current) && depth < 64) {
        current = parentOf.get(current);
        depth += 1;
      }
      if (depth > deepest) deepest = depth;
    }

    return deepest;
  }, [nodes, edges]);

  const directActives = referralList.filter(
    (ref) => ref.network?.loyalty_status === true
  ).length;

  if (isUserLoading || isNetworkLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!mainUser) {
    return <div className="ui-empty">Usuario no encontrado.</div>;
  }

  const totalDirects = referralList?.length || 0;
  const totalNetwork = mainUser?.user_network?.length || totalDirects;

  const stats = [
    { title: 'Red total', value: totalNetwork, icon: 'ri-team-line', color: '#4B36F0' },
    { title: 'Directos', value: totalDirects, icon: 'ri-user-shared-line', color: '#4B36F0' },
    { title: 'Directos activos', value: directActives, icon: 'ri-user-follow-line', color: '#0F9A5A' },
    { title: 'Niveles a la vista', value: levelsShown, icon: 'ri-stack-line', color: '#7C3AED' },
  ];

  return (
    <>
      <PageHeader
        title="Red de referidos"
        crumbs={[{ label: 'Usuarios', to: '/user' }, { label: 'Red' }]}
        actions={
          <button type="button" className="ui-btn" onClick={() => navigate(-1)}>
            <i className="ri-arrow-left-line" /> Volver
          </button>
        }
      />

      <div className="ui-grid-kpis mb-4">
        {stats.map((stat) => (
          <div key={stat.title} className="ui-card ui-kpi ui-kpi--inline">
            <Hexagon size={46} color={stat.color} icon={stat.icon} />
            <div>
              <div className="ui-kpi__title">{stat.title}</div>
              <div className="ui-kpi__value" style={{ color: stat.color }}>
                {stat.value.toLocaleString('es-CO')}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="ui-table-card">

        <div className="ui-table-card__filters">
          <div className="d-flex align-items-center gap-3">
            <img
              src={`${API_BASE_URL}/users/show/photo/${mainUser?.photo || ''}`}
              alt={mainUser?.name || 'Usuario'}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                objectFit: 'cover',
                background: '#EDEFF6',
              }}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{mainUser.name}</div>
              <div className="ui-mono">
                {mainUser.multyhealth_code} · raíz de la red
              </div>
            </div>
          </div>

          <div
            className="ui-tree__legend"
            style={{ marginLeft: 20, paddingLeft: 20, borderLeft: '1px solid #EDEFF6' }}
          >
            <span><i /> Activo</span>
            <span><i className="is-off" /> Inactivo</span>
          </div>

          <button
            type="button"
            className="ui-btn ui-btn--sm ms-auto"
            onClick={() => flow?.fitView({ padding: 0.15 })}
          >
            <i className="ri-focus-3-line" /> Ajustar
          </button>
        </div>

        {/*
          El lienzo degrada a scroll, no recorta: capa interna con min-width
          dentro de un contenedor con overflow auto, igual que las tablas.
        */}
        <div className="ui-tree ui-tree--tall">
          <div className="ui-tree__scroll">
            <div className="ui-tree__layer">
              <ReactFlow
                nodeTypes={NODE_TYPES}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onInit={setFlow}
                defaultEdgeOptions={EDGE_OPTIONS}
                proOptions={{ hideAttribution: true }}
                fitView
              >
                <MiniMap
                  position="bottom-left"
                  pannable
                  zoomable
                  nodeColor={(node) =>
                    node.data?.isRoot ? '#4B36F0' : '#9DA5C4'
                  }
                  maskColor="rgba(246, 247, 251, .7)"
                  style={{ border: '1px solid #E1E4F0', borderRadius: 9 }}
                />
                <Controls position="bottom-right" showInteractive={false} />
                <Background
                  variant={BackgroundVariant.Dots}
                  gap={22}
                  size={1.5}
                  color={DOT_COLOR}
                />
              </ReactFlow>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Información del usuario
        </ModalHeader>
        <ModalBody>
          {modalData && (
            <>
              <div className="text-center mb-3">
                <img
                  src={`${API_BASE_URL}/users/show/photo/${modalData.photo || ''}`}
                  alt={modalData.name || 'Usuario'}
                  className="rounded-circle mb-2"
                  width={80}
                  height={80}
                  style={{ objectFit: 'cover', background: '#EDEFF6' }}
                />
                <h5 className="fw-bold mb-1">{modalData.name}</h5>
                <div className="ui-mono">{modalData.multyhealth_code}</div>
              </div>

              <div className="ui-kv">
                <div className="ui-kv__row">
                  <span className="ui-kv__label">Correo</span>
                  <span className="ui-kv__value">{modalData.email || '—'}</span>
                </div>
                <div className="ui-kv__row">
                  <span className="ui-kv__label">Teléfono</span>
                  <span className="ui-kv__value">{modalData.cellphone || '—'}</span>
                </div>
                <div className="ui-kv__row">
                  <span className="ui-kv__label">Estado</span>
                  <span className="ui-kv__value">
                    <StatusChip
                      tone={modalData.network?.loyalty_status ? 'success' : 'danger'}
                      icon={
                        modalData.network?.loyalty_status
                          ? 'ri-checkbox-circle-line'
                          : 'ri-close-circle-line'
                      }
                    >
                      {modalData.network?.loyalty_status ? 'Activo' : 'Inactivo'}
                    </StatusChip>
                  </span>
                </div>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};
